import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, Palette } from 'lucide-react';
import { SlaThreshold } from '@/types/pipeline';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent } from '@/components/ui/card';
import { SlidePanel } from '@/components/ui/slide-panel';

interface SlaSettingsProps {
  slaEnabled: boolean;
  onSlaEnabledChange: (enabled: boolean) => void;
  slaSettings: SlaThreshold[];
  onSlaSettingsChange: (settings: SlaThreshold[]) => void;
}

const DEFAULT_COLORS = [
  '#fbbf24', // Yellow
  '#ef4444', // Red
  '#3b82f6', // Blue
  '#10b981', // Green
  '#8b5cf6', // Purple
  '#f97316', // Orange
  '#ec4899', // Pink
  '#6b7280', // Gray
];

export function SlaSettings({
  slaEnabled,
  onSlaEnabledChange,
  slaSettings,
  onSlaSettingsChange,
}: SlaSettingsProps) {
  // Ensure slaSettings is always an array
  const safeSlaSettings = Array.isArray(slaSettings) ? slaSettings : [];
  const [localSettings, setLocalSettings] = useState<SlaThreshold[]>(safeSlaSettings);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newThreshold, setNewThreshold] = useState<SlaThreshold>({
    days: 0,
    color: DEFAULT_COLORS[0],
    label: '',
  });

  useEffect(() => {
    // Ensure slaSettings is always an array
    const safeSlaSettings = Array.isArray(slaSettings) ? slaSettings : [];
    setLocalSettings(safeSlaSettings);
  }, [slaSettings]);

  const handleAddThreshold = () => {
    // Reset new threshold form
    setNewThreshold({
      days: 0,
      color: DEFAULT_COLORS[0],
      label: '',
    });
    setIsAddDialogOpen(true);
  };

  const handleSaveNewThreshold = () => {
    if (!newThreshold.label.trim()) {
      return; // Don't save if label is empty
    }

    const updatedSettings = [...localSettings, newThreshold];
    
    // Auto-sort by days (ascending)
    const sortedSettings = updatedSettings.sort((a, b) => a.days - b.days);
    
    setLocalSettings(sortedSettings);
    onSlaSettingsChange(sortedSettings);
    setIsAddDialogOpen(false);
  };

  const handleRemoveThreshold = (index: number) => {
    const updatedSettings = localSettings.filter((_, i) => i !== index);
    
    // Auto-sort by days (ascending) - though this shouldn't change order after removal
    const sortedSettings = [...updatedSettings].sort((a, b) => a.days - b.days);
    
    setLocalSettings(sortedSettings);
    onSlaSettingsChange(sortedSettings);
  };

  const handleUpdateThreshold = (index: number, field: keyof SlaThreshold, value: string | number) => {
    const updatedSettings = localSettings.map((threshold, i) => {
      if (i === index) {
        return { ...threshold, [field]: value };
      }
      return threshold;
    });
    
    // Auto-sort by days (ascending)
    const sortedSettings = [...updatedSettings].sort((a, b) => a.days - b.days);
    
    setLocalSettings(sortedSettings);
    onSlaSettingsChange(sortedSettings);
  };



  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="space-y-3 pt-2 pb-4">
          <p className="text-sm text-muted-foreground break-words whitespace-normal">
            Configure SLA thresholds for this stage. Projects will be highlighted with the specified colors when they exceed the day limits.
          </p>
              
          {slaEnabled ? (
            <div className="space-y-3">
              {localSettings.map((threshold, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-semibold text-foreground">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-medium">Days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={threshold.days}
                      onChange={(e) => handleUpdateThreshold(index, 'days', parseInt(e.target.value) || 0)}
                      className="w-20"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <Label className="text-sm font-medium">Label</Label>
                    <Input
                      value={threshold.label}
                      onChange={(e) => handleUpdateThreshold(index, 'label', e.target.value)}
                      placeholder="e.g., Warning"
                    />
                  </div>
                  
                  <div className="flex items-end gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-10 h-10 p-0"
                          style={{ backgroundColor: threshold.color }}
                        >
                          <Palette className="h-4 w-4 text-white" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48">
                        <div className="grid grid-cols-4 gap-2">
                          {DEFAULT_COLORS.map((color) => (
                            <button
                              key={color}
                              className="w-8 h-8 rounded border-2 border-border hover:border-border transition-colors"
                              style={{ backgroundColor: color }}
                              onClick={() => handleUpdateThreshold(index, 'color', color)}
                            />
                          ))}
                        </div>
                        <div className="mt-2">
                          <Input
                            type="color"
                            value={threshold.color}
                            onChange={(e) => handleUpdateThreshold(index, 'color', e.target.value)}
                            className="w-full"
                          />
                        </div>
                      </PopoverContent>
                    </Popover>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveThreshold(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={handleAddThreshold}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add SLA Threshold
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Enable SLA tracking to set time-based alerts for projects in this stage.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add SLA Threshold Panel */}
      <SlidePanel
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        title="Add SLA Threshold"
        width="md"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newThresholdDays">Days</Label>
              <Input
                id="newThresholdDays"
                type="number"
                min="0"
                value={newThreshold.days}
                onChange={(e) => setNewThreshold({ ...newThreshold, days: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newThresholdLabel">Label *</Label>
              <Input
                id="newThresholdLabel"
                value={newThreshold.label}
                onChange={(e) => setNewThreshold({ ...newThreshold, label: e.target.value })}
                placeholder="e.g., On Track, Warning, Critical"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-12 h-12 p-0"
                      style={{ backgroundColor: newThreshold.color }}
                    >
                      <Palette className="h-5 w-5 text-white" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48">
                    <div className="grid grid-cols-4 gap-2">
                      {DEFAULT_COLORS.map((color) => (
                        <button
                          key={color}
                          className="w-8 h-8 rounded border-2 border-border hover:border-border transition-colors"
                          style={{ backgroundColor: color }}
                          onClick={() => setNewThreshold({ ...newThreshold, color })}
                        />
                      ))}
                    </div>
                    <div className="mt-2">
                      <Input
                        type="color"
                        value={newThreshold.color}
                        onChange={(e) => setNewThreshold({ ...newThreshold, color: e.target.value })}
                        className="w-full"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
                <span className="text-sm text-muted-foreground">
                  {newThreshold.label || 'Preview'}
                </span>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4 mt-6">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveNewThreshold}
                disabled={!newThreshold.label.trim()}
              >
                Add Threshold
              </Button>
            </div>
          </div>
      </SlidePanel>
    </>
  );
} 